"use client"
import styled from "@emotion/styled"
import { Tab, Tabs } from "@mui/material"
import { useState } from "react"
import { NewText } from "ui"
import { sharedColors } from "ui/theme/colors"
import FormComponent, { SubmitResultPayload } from "./FormComponent"

const StyledSection = styled.section`
  width: 100%;
  padding: var(--form-section-padding-mobile, 24px 0);
  border-radius: 16px;
  background: #fff;
  margin-bottom: var(--form-section-margin-bottom, 40px);

  /* Card Dark */
  box-shadow: var(--form-section-box-shadow, 0 4px 12px 0 rgba(0, 0, 0, 0.16));
  background: var(--White, #fff);
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: var(--form-section-padding-desktop, 32px 0);
  }
`

const StyledTopSection = styled.div`
  padding: var(--form-title-padding-mobile, 0 16px);

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: var(--form-title-padding-desktop, 0 24px);
  }
`

const StyledTabs = styled(Tabs)`
  padding: 8px;
  background-color: #f7f7f7;
  display: inline-flex;
  margin: 20px 0;
  border-radius: 12px;
  min-height: unset;
  border-bottom: none;

  .MuiTabs-indicator {
    display: none;
  }
  .MuiTabs-scroller {
    overflow: visible !important;
  }
  .MuiTabs-flexContainer {
    align-items: center;
    justify-content: space-between;
  }

  @media (min-width: 370px) {
    .MuiTabs-flexContainer {
      display: grid;
      grid-template-columns: 1fr 1fr;
    }
  }

  ${(props) => props.theme.breakpoints.up("sm")} {
    margin: 24px 0;
    .MuiTabs-flexContainer {
      display: inline-flex !important;
    }
  }
`

const StyledTab = styled(Tab)<{ $active: boolean }>`
  padding: 8.5px 6px;
  display: inline-flex;
  gap: 6px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  font-family: var(--form-switch-font, inherit) !important;
  background: ${(p) =>
    p.$active ? "var(--form-switch-active-bg, var(--Blue-Main, #2768a3))" : "transparent"};
  color: ${(p) => (p.$active ? "#FFFFFF" : "black")};
  opacity: 1;
  margin: 0;
  font-style: normal;
  font-weight: ${(p) => (p.$active ? "700" : "400")};
  font-size: 18px !important;
  line-height: 150% !important;
  letter-spacing: 0.02857em !important;
  text-transform: none;
  text-decoration: none;
  min-width: 96px;
  min-height: unset;
  height: 43px;
  white-space: nowrap;

  &.Mui-selected {
    color: #ffffff;
  }

  .MuiTouchRipple-root {
    display: none;
  }

  svg,
  path {
    fill: inherit;
  }
  svg {
    height: 22px;
    width: 30px;
    min-width: 30px;
  }
  &:hover {
    text-decoration: none !important;
    background: ${(p) =>
      p.$active ? "var(--form-switch-active-bg, var(--Blue-Main, #2768a3))" : "transparent"};
    color: ${(p) => (p.$active ? "#FFFFFF" : "black")};
  }
  &:focus-visible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }
  @media (min-width: 370px) {
    width: unset;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    min-width: 163px;
    padding: 8px 12px;
  }
`

type Props = {
  orderId: number
  jobType?: any
  messages?: any
  setModalOpen?: any
  isHot?: boolean
  requireCv?: boolean
  onValidSubmit?: () => void
  // eslint-disable-next-line no-unused-vars
  onSubmitResult?: (payload: SubmitResultPayload) => void
  disableSuccessRedirect?: boolean
}

type FormSectionBaseProps = Props & {
  variant?: "page" | "modal"
  className?: string
}

const FormSectionBase = ({
  orderId,
  jobType,
  messages,
  isHot = false,
  requireCv,
  variant = "page",
  className,
  onValidSubmit,
  onSubmitResult,
  disableSuccessRedirect,
}: FormSectionBaseProps) => {
  const isModal = variant === "modal"
  const [formType, setFormType] = useState<"regular" | "employee">("regular")
  return (
    <StyledSection className={className}>
      <StyledTopSection>
        {!isModal && (
          <NewText
            component="h2"
            desktopFontSize={32}
            desktopLineHeight="36px"
            desktopFontWeight={"700"}
            color={sharedColors.black}
            sx={{
              marginBlock: {
                xs: 0,
              },
            }}
          >
            {"בואו לעבוד איתנו"}
          </NewText>
        )}
        <StyledTabs
          value={formType}
          onChange={(_, newValue) => setFormType(newValue)}
          aria-label="בחירת סוג טופס"
        >
          <StyledTab
            value="regular"
            label="מעוניין/ת להצטרף"
            $active={formType === "regular"}
            sx={{ fill: formType === "employee" ? "black" : "#6F6F6F" }}
          />
          <StyledTab
            value="employee"
            label="חבר מביא חבר"
            $active={formType === "employee"}
            sx={{ fill: formType === "regular" ? "black" : "#6F6F6F" }}
          />
        </StyledTabs>
      </StyledTopSection>
      <FormComponent
        orderId={orderId}
        jobType={jobType}
        messages={messages}
        isReferral={formType === "employee"}
        isHot={isHot}
        requireCv={requireCv}
        onValidSubmit={onValidSubmit}
        onSubmitResult={onSubmitResult}
        disableSuccessRedirect={disableSuccessRedirect}
      />
    </StyledSection>
  )
}

const FormSection = styled(FormSectionBase)`
  --form-section-margin-bottom: 40px;
  --form-section-box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.16);
  --form-title-padding-mobile: 0 16px;
  --form-title-padding-desktop: 0 24px;
  --form-switch-active-bg: var(--Blue-Main, #2768a3);
  --form-align: center;
`

const FormSectionModal = styled((props: Omit<FormSectionBaseProps, "variant">) => (
  <FormSectionBase {...props} variant="modal" />
))`
  --form-section-margin-bottom: 0;
  --form-section-box-shadow: none;
  --form-section-padding-mobile: 32px 0 0;
  --form-section-padding-desktop: 32px 0 0;
  --form-title-padding-mobile: 0;
  --form-title-padding-desktop: 0 24px;
  --form-switch-active-bg: ${sharedColors.darkBlue4rentMarketing};
  --form-align: flex-end;
  --form-inside-padding-mobile: 16px 1px;
  --form-inside-padding-desktop: 16px 1px;
  --form-switch-font: var(--narkis-condensed);
  --form-button-bg: ${sharedColors.blue4rentMarketing};
  --form-button-color: ${sharedColors.white};
  --form-button-border: ${sharedColors.blue4rentMarketing};
  --form-button-hover-bg: ${sharedColors.blue4rentMarketing};
  --form-button-hover-color: ${sharedColors.white};
  --form-button-hover-border: ${sharedColors.blue4rentMarketing};
  --form-button-width-mobile: 100%;
  --form-button-max-width-mobile: 100%;
  --form-button-width-desktop: 169px;
  --form-button-max-width-desktop: 169px;
  --form-button-margin-top-mobile: 0;
  --form-button-margin-top-desktop: 0px;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex: 1;
    overflow: auto;
    --form-padding-bottom: 50px;
    --form-position: static;
    --form-align-items: center;
    --form-align-height: 80px;
    --form-align-width: 100%;
    --form-align-position: absolute;
    --form-align-z-index: 1;
    --form-align-right: 0;
    --form-align-top: 100%;
    --form-align-transform: translateY(-100%);
    --form-align-bg: white;
    --form-overflow: visible;
    --form-recaptcha-position: relative;
    --form-recaptcha-z-index: 2;
  }

  .MuiInputBase-input,
  .MuiInputLabel-root,
  .MuiFormHelperText-root,
  p {
    font-weight: 300 !important;

    ${(props) => props.theme.breakpoints.down("md")} {
      font-size: 18px;
    }
  }

  button:not([type="submit"]) {
    font-weight: 300 !important;
    font-family: var(--narkis-condensed) !important;
  }
`

export { FormSectionModal }
export default FormSection
